import { useEffect } from "react";
import { FormMessage } from "@/components/ui/form";
import { SelectItem } from "@/components/ui/select";
import { TenderType } from "@/graphql/__generated__/graphql";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  InputField,
  SelectInputField,
  TextareaInputField,
  CheckboxInputField,
  ComboboxInputField,
} from "@/components/shared/form";
import { provinces } from "@/constants/provinces";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RenderFormRowComponent from "./RenderFormRowComponent";

export const stringToNumber = (val: unknown) => (typeof val === "string" ? parseFloat(val) : val);

/**
 * Creates a zod schema for the description form group
 * All fields are requirements are based off quickoffer tender type, public and private tenders validation handled in main schema
 * @param t - The translation function
 * @returns The zod schema
 */
export const createDescriptionSchema = (t: TFunction) => {
  return z.object({
    description: z.object({
      name: z.string().min(2, {
        message: t("schemas:createtender.description.namemin"),
      }),
      province: z.string().min(1, {
        message: t("schemas:createtender.description.provincerequired"),
      }).optional(),
      type: z.enum([TenderType.Public, TenderType.Private, TenderType.QuickOffer], {
        message: t("schemas:createtender.description.typerequired"),
      }),
      platform: z.string().optional(),
      awardingAuthority: z.string().optional(),
      details: z
        .string()
        .max(500, {
          message: t("schemas:createtender.description.detailsmax"),
        }).optional(),
      permanent: z.boolean(),
      mobile: z.boolean(),
      alarm: z.boolean(),
    }).check((ctx) => {
      if (ctx.value.type === TenderType.Public && !ctx.value.platform) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.platformmin"),
          path: ["platform"],
          input: ctx.value.platform,
        });
      }

      if (ctx.value.type === TenderType.Public && !ctx.value.awardingAuthority) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:form.required"),
          path: ["awardingAuthority"],
          input: ctx.value.awardingAuthority,
        });
      }
    })
  });
};

/**
 * DescriptionFormGroup component for editing the description form group
 * Utilises newtender locales as these values are based off the new tender form, if newtender changes, these will change with it.
 * @param form - The form object
 * @returns The rendered description form group component
 */
export function DescriptionFormGroup({ form }: { form: UseFormReturn<any> }) {
  const { formState } = form;

  // Constants
  const tenderType = form.watch("description.type"); // Watch the tender type value from the form

  // FIXME: For some reason description.type is showing undefined error when serviceType has a custom error
  const serviceTypeErrorMessage = formState.errors.description?.["serviceType"]?.message;

  // Hooks
  const { t } = useTranslation();

  useEffect(() => {
    if (tenderType === TenderType.Private) {
      // Clear errors for platform when type is private
      form.clearErrors("description.platform");
      // Reset the platform field value
      form.setValue("description.platform", undefined);
    }
  }, [tenderType, form.clearErrors, form.setValue]);

  return (
    <Card id="description">
      <CardHeader className="pt-4 pb-2 select-none">
        <CardTitle className="text-lg">{t("newtender.formgroups.description.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <div>
          <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.name.label")}>
            <InputField
              form={form}
              name="description.name"
              className="w-full"
              placeholder={t("newtender.formgroups.description.inputs.name.placeholder")}
            />
          </RenderFormRowComponent>

          <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.province.label")}>
            <ComboboxInputField
              form={form}
              name="description.province"
              options={provinces.map((province) => ({
                label: province.name,
                value: province.code
              }))}
              className="w-full"
            />
          </RenderFormRowComponent>

          <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.type.label")}>
            <SelectInputField
              form={form}
              name="description.type"
              className="w-full"
            >
              {Object.values(TenderType).map((type) => (
                <SelectItem
                  key={type}
                  value={type}
                  disabled={type === TenderType.QuickOffer}
                >
                  {t("common:tendertype." + type.toLowerCase())}
                </SelectItem>
              ))}
            </SelectInputField>
          </RenderFormRowComponent>

          {tenderType === TenderType.Public && (
            <>
              <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.platform.label")}>
                <InputField
                  form={form}
                  name="description.platform"
                  className="w-full"
                  placeholder={t("newtender.formgroups.description.inputs.platform.label")}
                />
              </RenderFormRowComponent>

              <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.awardingauthority.label")}>
                <InputField
                  form={form}
                  name="description.awardingAuthority"
                  className="w-full"
                  placeholder={t("newtender.formgroups.description.inputs.awardingauthority.label")}
                />
              </RenderFormRowComponent>
            </>
          )}

          <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.details.label")}>
            <TextareaInputField
              form={form}
              name="description.details"
              isFormItem={false}
              placeholder={t("newtender.formgroups.description.inputs.details.placeholder")}
            />
          </RenderFormRowComponent>

          {/* Service type */}
          <RenderFormRowComponent label={t("newtender.formgroups.description.inputs.servicetype.label")}>
            <div className="flex gap-2 justify-start">
              <CheckboxInputField
                form={form}
                name="description.permanent"
                className="w-full"
                label={t("newtender.formgroups.description.inputs.servicetype.options.permanent")}
              />
              <CheckboxInputField
                form={form}
                name="description.mobile"
                className="w-full"
                label={t("newtender.formgroups.description.inputs.servicetype.options.mobile")}
              />
              <CheckboxInputField
                form={form}
                name="description.alarm"
                className="w-full"
                label={t("newtender.formgroups.description.inputs.servicetype.options.alarm")}
              />

            </div>
            {serviceTypeErrorMessage && (
              <FormMessage className="mt-2">
                {typeof serviceTypeErrorMessage === "string" ? serviceTypeErrorMessage : null}
              </FormMessage>
            )}
          </RenderFormRowComponent>
        </div>
      </CardContent>
    </Card>
  );
}
